import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { DriverQualificationService } from '../../driver-qualification-files/service/driver-qualification.service';
import { MatBottomSheetRef, MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import dayjs from 'dayjs';
import { Router } from '@angular/router';
import { ToastService } from 'src/app/shared/toast/toast.service';
import { OPTIONAL_DRIVER_DOCS, DQF_DOC_STATUS } from '../constants';
import { Observable, of, timer } from 'rxjs';
import { switchMap, catchError, map } from 'rxjs/operators';

const STATUS_DQF_DRIVER_SIGNATURE_SUBMITTED = 'DriverSignatureSubmitted';
const UPLOADED_DOCUMENT = 'DQF successfully sent for approval';
const SUBMIT_FAILED_FALLBACK = 'The driver qualification signature submission failed';

@Component({
    selector: 'app-dqf-signature-panel',
    templateUrl: './dqf-signature-panel.component.html',
    styleUrls: ['./dqf-signature-panel.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule
    ]
})
export class DqfSignaturePanelComponent implements OnInit {
  signatureForm: UntypedFormGroup;
  driverSignatureIntent: string;
  isClicked = false;
  dqfId: string;
  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly driverQualificationService: DriverQualificationService,
    public dialogRef: MatBottomSheetRef<DqfSignaturePanelComponent>,
    public router: Router,
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: any,
    private readonly toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.getDriverSignatureIntent();
    this.signatureForm = this.createForm();

    this.dqfId = this.data?.dqfId;
  }

  onSubmit(): void {
    if (!this.dqfId || !this.signatureForm.valid || !this.driverSignatureIntent) {
      return;
    }

    this.isClicked = true;

    // Normalize skipped optional docs to Pending, but never undo CarrierUploadRequested
    this.markOptionalDocumentsIfNeeded()
      .pipe(
        switchMap(() => this.submitDqfSignature()),
        catchError(error => {
          this.isClicked = false;
          this.toastService.showWarning(this.getErrorMessage(error));
          return of(null);
        })
      )
      .subscribe();
  }

  private markOptionalDocumentsIfNeeded(): Observable<null> {
    return OPTIONAL_DRIVER_DOCS.reduce(
      (previous$, documentType) =>
        previous$.pipe(
          switchMap(anyUpdated =>
            this.markDocumentPendingIfNeeded(documentType).pipe(map(updated => anyUpdated || updated))
          )
        ),
      of(false)
    ).pipe(switchMap(didUpdate => (didUpdate ? this.waitUntilNextUtcSecond() : of(null))));
  }

  private markDocumentPendingIfNeeded(documentType: string): Observable<boolean> {
    const attachedDoc = this.driverQualificationService.getDqfDocumentFromAttachedDocuments(documentType);

    // Keep CarrierUploadRequested — Pending here undoes "Request Carrier" on the backend
    const needsUpdate =
      attachedDoc &&
      !attachedDoc.isRequirementsCompleted &&
      attachedDoc.status !== DQF_DOC_STATUS.PENDING &&
      attachedDoc.status !== DQF_DOC_STATUS.CARRIER_UPLOAD_REQUESTED;

    if (!needsUpdate) {
      return of(false);
    }

    const documentUpdate = {
      attachedDocument: {
        type: documentType,
        status: DQF_DOC_STATUS.PENDING
      }
    };

    return this.driverQualificationService.updateDqfStatus(this.dqfId, documentUpdate).pipe(
      switchMap(() => this.driverQualificationService.getDqfById(this.dqfId)),
      map(() => true),
      catchError(() => of(false))
    );
  }

  /**
   * Backend DQF modifiedDate is second-precision. A Pending PATCH then immediate
   * signature submit in the same second fails the DynamoDB LESS_THAN condition (4007).
   */
  private waitUntilNextUtcSecond(): Observable<null> {
    const delayMs = 1000 - (Date.now() % 1000) + 50;
    return timer(delayMs).pipe(map(() => null));
  }

  private submitDqfSignature() {
    const dqfSignatureStatus = {
      status: STATUS_DQF_DRIVER_SIGNATURE_SUBMITTED,
      driverSignature: {
        capturedInitials: this.signatureForm.value.initial,
        driverSignatureIntentId: this.driverSignatureIntent
      }
    };

    return this.driverQualificationService.updateDqfStatus(this.dqfId, dqfSignatureStatus).pipe(
      switchMap(res => {
        this.toastService.showSuccess(UPLOADED_DOCUMENT);
        this.dialogRef.dismiss();
        this.router.navigate(['']);
        return of(res);
      })
    );
  }

  private getErrorMessage(error: any): string {
    return error?.error?.message || error?.message || SUBMIT_FAILED_FALLBACK;
  }

  private createForm(): UntypedFormGroup {
    const currentDate = dayjs(new Date()).format('DD MMM YYYY');
    return this.fb.group({
      initial: ['', Validators.required],
      date: [currentDate, Validators.required]
    });
  }

  private getDriverSignatureIntent(): void {
    this.driverQualificationService.getDriverSignatureIntent().subscribe(
      response => {
        if (response && response.data) {
          this.driverSignatureIntent = response.data.id;
        }
      },
      error => {}
    );
  }
}
